/**
 * Helper condivisi per leggere i valori restituiti dall'API e formattarli.
 *
 * L'API restituisce i campi `option` come `{ value, label }` e i campi
 * `externalEntity` come oggetto completo (o `null`): queste funzioni
 * normalizzano entrambi i casi, accettando anche il valore grezzo.
 */

export const MESI = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

/** Indice 0 = lunedi, come la colonna `giorno_settimana` (1..7). */
export const GIORNI = ['Lunedi', 'Martedi', 'Mercoledi', 'Giovedi', 'Venerdi', 'Sabato', 'Domenica'];

export const GIORNI_BREVI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Valore di un campo `option`, che arrivi come oggetto o come scalare. */
export function optValue(field) {
    if (field === null || typeof field === 'undefined') return '';
    if (typeof field === 'object') return field.value;
    return field;
}

/** Id di un campo `externalEntity`, che arrivi come oggetto o come scalare. */
export function refId(field) {
    if (field === null || typeof field === 'undefined' || field === '') return 0;
    if (typeof field === 'object') return parseInt(field.id, 10) || 0;
    return parseInt(field, 10) || 0;
}

/** Etichetta leggibile di un campo `externalEntity`, provando piu' campi. */
export function refLabel(field, fields, fallback) {
    if (!field || typeof field !== 'object') return fallback || '—';
    const candidati = fields || ['nome', 'titolo', 'identificativo', 'name'];
    for (let i = 0; i < candidati.length; i++) {
        if (field[candidati[i]]) return field[candidati[i]];
    }
    return fallback || '—';
}

/** Nome e cognome di un utente restituito dall'API. */
export function nomeUtente(user) {
    if (!user) return '—';
    const nome = `${user.name || ''} ${user.surname || ''}`.trim();
    return nome || user.username || user.email || '—';
}

/** Le iniziali al posto della foto: due lettere bastano a riconoscersi. */
export function iniziali(nome) {
    const lettere = String(nome || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(parola => parola[0]);
    return lettere.join('').toUpperCase();
}

/** Payload per un campo `option` accettato da DataManager. */
export function optPayload(value) {
    return { value: value };
}

/** Payload per un campo `externalEntity`. 0 = nessun collegamento. */
export function refPayload(id) {
    return { id: parseInt(id, 10) || 0 };
}

/** 'YYYY-MM-DD HH:MM:SS' -> Date. Il replace serve a Safari. */
export function parseSql(value) {
    if (!value) return null;
    const d = new Date(String(value).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
}

/** Date -> 'YYYY-MM-DD' in ora locale (toISOString userebbe UTC). */
export function toDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const g = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${g}`;
}

/** Date -> 'YYYY-MM-DD HH:MM:SS'. */
export function toSqlDateTime(date) {
    const h = String(date.getHours()).padStart(2, '0');
    const i = String(date.getMinutes()).padStart(2, '0');
    return `${toDateKey(date)} ${h}:${i}:00`;
}

/** '2026-03-05' -> '05/03/2026' */
export function formatDate(value) {
    const d = parseSql(value);
    if (!d) return '—';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** '2026-03-05 14:00:00' -> '05/03/2026, 14:00' */
export function formatDateTime(value) {
    const d = parseSql(value);
    if (!d) return '—';
    return d.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

/** '14:00:00' -> '14:00' */
export function formatTime(value) {
    if (!value) return '—';
    return String(value).substring(0, 5);
}

/** Intervallo compatto: '05/03/2026 · 14:00 – 18:00' */
export function formatIntervallo(inizio, fine) {
    const a = parseSql(inizio);
    const b = parseSql(fine);
    if (!a) return '—';
    const giorno = a.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const ora = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return b ? `${giorno} · ${ora(a)} – ${ora(b)}` : `${giorno} · ${ora(a)}`;
}

/** 12.5 -> '12,50 €' */
export function formatImporto(value) {
    const n = parseFloat(value);
    if (isNaN(n)) return '—';
    return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

/** 'evento_ricorrente' -> 'Evento ricorrente' */
export function umanizza(value) {
    if (!value) return '—';
    const s = String(value).replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Primo giorno del mese di `date`. */
export function inizioMese(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Ultimo giorno del mese di `date`. */
export function fineMese(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/** Nuova data spostata di `giorni`. */
export function aggiungiGiorni(date, giorni) {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + giorni);
    return d;
}

/** 'HH:MM' o 'HH:MM:SS' -> minuti dalla mezzanotte. */
export function oraInMinuti(value) {
    if (!value) return 0;
    const parti = String(value).split(':');
    return (parseInt(parti[0], 10) || 0) * 60 + (parseInt(parti[1], 10) || 0);
}
